/* eslint-disable max-lines */
import { useCallback, useEffect, useRef, useState } from "react"

import type { VoiceEngine } from "../utils/voice-engine"
import type { ActivityMessage, ToolApproval } from "@/api/models"

import { extractFairUseError, type FairUseErrorCode } from "@/features/fair-use/fair-use-utils"
import { useVoiceSettings } from "@/hooks/use-voice-settings"
import { logger } from "@/lib/logger"
import { SettingsService } from "@/services/settings-service"

import { createToolActivity } from "./voice-mode-handler-utils"
import { buildLiveStartFrame, isLiveBlockedClientEvent } from "../utils/live-session-config"
import { buildRealtimeSessionUpdate } from "../utils/realtime-session-config"
import { normalizeVoiceActivityFrame } from "../utils/voice-activity-feed"
import { hasVoiceInternalDataConsent } from "../utils/voice-mode-consent"
import {
  isVoicePlumbingTool,
  reduceVoiceTranscript,
  type VoiceTranscriptEntry,
  type VoiceTranscriptEvent,
} from "../utils/voice-transcript"

export type RealtimeAudioState =
  | "idle" // Not in voice mode
  | "connecting" // Connecting to WebSocket
  | "listening" // In voice mode, waiting for user to speak
  | "user_speaking" // User is speaking (VAD detected)
  | "processing" // AI is thinking
  | "using_tools" // AI is executing tools (web search, etc.)
  | "ai_speaking" // AI is responding with audio
  | "interrupted" // GPT-Live: the model stopped because the user spoke over it
  | "reconnecting" // GPT-Live: the transport dropped and one retry is in flight

export type RealtimeCompletedTool = {
  name: string
  callId?: string
  success: boolean
  assistantFiles: string[]
}

export type RealtimeCompletionActivity = {
  activityType: string
  message: string
  details?: Record<string, unknown>
  timestamp?: number
}

export type RealtimeAssistantCompletion = {
  toolCalls: RealtimeCompletedTool[]
  activities: RealtimeCompletionActivity[]
  assistantFiles: string[]
  inputItemId?: string
  /**
   * GPT-Live only: the model spoke without a caller turn to answer (a
   * greeting, a follow-up). Such an answer has no input item BY DESIGN, so it
   * must never be held for a "late" user transcript the way an id-less
   * Realtime completion is.
   */
  unprompted?: boolean
}

export type RealtimeTranscriptionFailure = {
  code?: string
  errorType?: string
  itemId?: string
}

export type RealtimeInterruptEvent =
  | {
      reason: "barge_in"
      responseId?: string
    }
  | {
      /** Provider output dropped for a tool handoff or a superseded native cancel. */
      reason: "response_output_discarded"
      responseId: string
      inputItemId?: string
    }
  | {
      reason: "response_discarded"
      responseId: string
      status: "cancelled" | "failed" | "incomplete"
      inputItemId?: string
    }
  | {
      /** The transport/session failed, so its active unverified turn must not be flushed. */
      reason: "session_failed"
      source: RealtimeSessionFailureSource
      responseId?: string
      inputItemId?: string
    }

/** How a voice session broke, for callers deciding what to do with its turn. */
export type RealtimeSessionFailureSource = "provider_error" | "socket_error" | "unexpected_close"

export type RealtimeAudioCallbacks = {
  onUserTranscript?: (text: string, itemId?: string) => void
  onAssistantTranscriptDelta?: (text: string, inputItemId?: string) => void
  onAssistantTranscriptDone?: (text: string, completion?: RealtimeAssistantCompletion) => void
  onTranscriptionFailure?: (failure: RealtimeTranscriptionFailure) => void
  onError?: (error: RealtimeAudioError) => void
  /**
   * A structured fair-use refusal frame, handed on verbatim so the consumer
   * can route it through `routeFairUseError` (which needs a QueryClient this
   * hook deliberately does not take). Always called alongside `onError`.
   */
  onFairUseRefusal?: (frame: unknown) => void
  onVoiceModeStart?: () => void
  onVoiceModeEnd?: () => void
  onAutoHangup?: (reason: RealtimeAutoHangupReason) => void
  onToolStart?: (toolName: string) => void
  onToolComplete?: (toolCall: RealtimeCompletedTool) => void
  onSessionCreated?: (sessionId: string) => void
  /**
   * Ends ownership of the active response. A barge-in may preserve the heard
   * partial exchange; a provider-discarded response must be removed fail closed.
   */
  onInterrupt?: (event?: RealtimeInterruptEvent) => void
}

/** GPT-Live refusals that mean "no voice right now", surfaced as unavailable copy. */
export type LiveVoiceError =
  | "live_voice_not_configured"
  | "live_session_limit"
  | "live_session_expired"
  | "live_content_filtered"
  | "live_connection_lost"

/**
 * Why voice mode stopped. The three transport failures plus the fair-use
 * refusals the backend sends as structured frames on the realtime socket
 * (`fair_use_blocked`, `plan_upgrade_required`): those are NOT service errors,
 * and rendering them as one told a user at 100 % of their budget that "voice
 * mode encountered a service error" with no budget card and no upgrade hint.
 */
export type RealtimeAudioError =
  | "microphone_access_failed"
  | "connection_failed"
  | "service_error"
  | FairUseErrorCode
  | LiveVoiceError

const LIVE_VOICE_ERRORS = new Set<string>([
  "live_voice_not_configured",
  "live_session_limit",
  "live_session_expired",
  "live_content_filtered",
  "live_connection_lost",
])

/**
 * `live_session_closed.code` values that use the named-error overlay. Hang-up
 * is `null` / missing. `live_connection_lost` on this frame is not retried:
 * the backend already persisted and closed the session.
 */
const LIVE_SESSION_CLOSED_ERROR_CODES = new Set<string>([
  "live_session_expired",
  "live_content_filtered",
])

/** True when this error came from the GPT-Live route rather than the transport. */
export function isLiveVoiceError(error: RealtimeAudioError | null | undefined): boolean {
  return typeof error === "string" && LIVE_VOICE_ERRORS.has(error)
}

export type RealtimeAutoHangupReason = "inactivity" | "maximum_duration"

export type RealtimeVoiceToolApproval = ToolApproval & {
  /** Backend-owned session binding that must be echoed with the decision. */
  sessionId: string
}

/** Raw VAD/noise does not reset this timer; only a non-empty user transcript does. */
export const VOICE_INACTIVITY_TIMEOUT_MS = 2 * 60 * 1000
/** Cost guard below the provider's 60-minute hard Realtime session limit. */
export const VOICE_MAX_SESSION_DURATION_MS = 20 * 60 * 1000
/** How long a tool may run before the overlay admits it is taking a while. */
export const VOICE_TOOL_STALL_NOTICE_MS = 8 * 1000
/**
 * Grace added to the backend's announced budget before the client gives up on
 * a tool call. This is a backstop for a backend that dies or a half-open
 * socket — the server normally sends its own terminal frame well inside it.
 */
export const VOICE_TOOL_WATCHDOG_GRACE_MS = 15 * 1000
/** Used only when a `tool_executing` frame arrives without a budget. */
export const VOICE_TOOL_WATCHDOG_FALLBACK_MS = 45 * 1000
/** Keeps the overlay feed to a glanceable window of the current turn. */
export const VOICE_ACTIVITY_FEED_LIMIT = 6
/** Initial playout reserve, expressed in media time rather than provider chunk count. */
export const VOICE_AUDIO_INITIAL_BUFFER_SECONDS = 0.03
/** Add this much reserve after a real playout underrun. */
export const VOICE_AUDIO_REBUFFER_STEP_SECONDS = 0.03
/** Keep jitter protection bounded so voice still feels realtime. */
export const VOICE_AUDIO_MAX_BUFFER_SECONDS = 0.12
/** Settle time before a Live socket drop is retried exactly once. */
export const LIVE_RECONNECT_DELAY_MS = 800
/** How long the overlay admits a barge-in before returning to the mic states. */
export const LIVE_INTERRUPTED_STATE_MS = 400
/**
 * After `voice_hangup` the socket stays open this long at most, waiting for
 * `live_session_closed`. The route persists the conversation before it sends
 * that frame, and `onVoiceModeEnd` refetches history, so ending the session
 * on the frame (not on the tap) is what makes the refetch read this call
 * rather than the one before it. Longer than the route's worst case (a 5 s
 * close drain plus the save); the window is already gone by then, so the
 * wait is not visible.
 */
export const LIVE_HANGUP_GRACE_MS = 8000
/** Warn once when the session has spent this share of its Live budget. */
export const LIVE_USAGE_WARNING_RATIO = 0.9

/** One in-flight tool call, as far as the voice surface is concerned. */
export type VoiceToolProgress = {
  name: string
  callId: string | null
  startedAt: number
  /** True once the call passes the stall threshold on either side of the wire. */
  stalled: boolean
}

type ActiveBackgroundJob = {
  name: string
  startedAt: number
  watchdogDeadline: number
}

// Audio Worklet Processor Code for capturing microphone audio
const WORKLET_CODE = `
class PCMProcessor extends AudioWorkletProcessor {
  process(inputs, outputs, parameters) {
    const input = inputs[0];
    if (input.length > 0) {
      const float32Data = input[0];
      const int16Data = new Int16Array(float32Data.length);
      for (let i = 0; i < float32Data.length; i++) {
        const s = Math.max(-1, Math.min(1, float32Data[i]));
        int16Data[i] = s < 0 ? s * 0x8000 : s * 0x7FFF;
      }
      this.port.postMessage(int16Data.buffer, [int16Data.buffer]);
    }
    return true;
  }
}
registerProcessor('pcm-processor', PCMProcessor);
`

export type UseRealtimeAudioOptions = {
  sessionId?: string
  sessionType?: string
  callbacks?: RealtimeAudioCallbacks
  /** Which voice backend to drive. Defaults to the Realtime route. */
  engine?: VoiceEngine
}

/**
 * Send one client frame, refusing the events GPT-Live has no equivalent for.
 * Realtime keeps the unconditional send it has always had.
 */
function sendVoiceFrame(
  socket: WebSocket | null,
  engine: VoiceEngine,
  frame: { type: string } & Record<string, unknown>
): boolean {
  if (!socket || socket.readyState !== WebSocket.OPEN) return false
  if (engine === "live" && isLiveBlockedClientEvent(frame.type)) {
    logger.warn("Voice mode: Suppressed a client event GPT-Live does not accept", {
      eventType: frame.type,
    })
    return false
  }
  socket.send(JSON.stringify(frame))
  return true
}

type QueuedAudioChunk = {
  buffer: AudioBuffer
  responseId: string
}

type ScheduledAudioSegment = {
  source: AudioBufferSourceNode
  responseId: string
  start: number
  end: number
}

function asRecord(value: unknown): Record<string, unknown> | null {
  return typeof value === "object" && value !== null ? (value as Record<string, unknown>) : null
}

/** The input item a frame belongs to, whether named at the top level or nested. */
function readInputItemId(message: Record<string, unknown>): string | null {
  if (typeof message.input_item_id === "string" && message.input_item_id) {
    return message.input_item_id
  }
  const response = asRecord(message.response)
  if (response && typeof response.input_item_id === "string" && response.input_item_id) {
    return response.input_item_id
  }
  return null
}

const TOOL_APPROVAL_STATUSES = new Set<ToolApproval["status"]>([
  "pending",
  "approved",
  "denied",
  "expired",
  "cancelled",
])

function normalizeVoiceToolApproval(
  message: Record<string, unknown>
): RealtimeVoiceToolApproval | null {
  const approval = asRecord(message.approval)
  const argumentsValue = approval ? asRecord(approval.arguments) : null
  const status = approval?.status
  if (
    typeof message.session_id !== "string" ||
    !message.session_id ||
    !approval ||
    typeof approval.request_id !== "string" ||
    !approval.request_id ||
    typeof approval.call_id !== "string" ||
    !approval.call_id ||
    typeof approval.tool !== "string" ||
    !approval.tool ||
    typeof approval.title !== "string" ||
    typeof approval.summary !== "string" ||
    typeof status !== "string" ||
    !TOOL_APPROVAL_STATUSES.has(status as ToolApproval["status"]) ||
    typeof approval.created_at !== "string" ||
    typeof approval.updated_at !== "string" ||
    !argumentsValue
  ) {
    return null
  }

  return {
    request_id: approval.request_id,
    call_id: approval.call_id,
    tool: approval.tool,
    title: approval.title,
    summary: approval.summary,
    arguments: argumentsValue,
    status: status as ToolApproval["status"],
    created_at: approval.created_at,
    updated_at: approval.updated_at,
    decision_user: typeof approval.decision_user === "string" ? approval.decision_user : null,
    reason: typeof approval.reason === "string" ? approval.reason : null,
    timeout_secs: typeof approval.timeout_secs === "number" ? approval.timeout_secs : null,
    sessionId: message.session_id,
  }
}

function sanitizeAssistantFiles(value: unknown): string[] {
  if (!Array.isArray(value)) return []
  return Array.from(
    new Set(
      value.filter(
        (filename: unknown): filename is string =>
          typeof filename === "string" && filename.length > 0
      )
    )
  )
}

function normalizeAssistantCompletion(
  message: Record<string, unknown>
): RealtimeAssistantCompletion {
  const toolCalls = (Array.isArray(message.tool_calls) ? message.tool_calls : []).flatMap(
    (value): RealtimeCompletedTool[] => {
      const toolCall = asRecord(value)
      if (!toolCall || typeof toolCall.name !== "string" || !toolCall.name) return []
      return [
        {
          name: toolCall.name,
          ...(typeof toolCall.call_id === "string" && toolCall.call_id
            ? { callId: toolCall.call_id }
            : {}),
          success: toolCall.success === true,
          assistantFiles: sanitizeAssistantFiles(toolCall.assistant_files),
        },
      ]
    }
  )
  const activities = (Array.isArray(message.activities) ? message.activities : []).flatMap(
    (value): RealtimeCompletionActivity[] => {
      const activity = asRecord(value)
      if (
        !activity ||
        typeof activity.activity_type !== "string" ||
        typeof activity.message !== "string"
      ) {
        return []
      }
      const details = asRecord(activity.details)
      return [
        {
          activityType: activity.activity_type,
          message: activity.message,
          ...(details ? { details } : {}),
          ...(typeof activity.timestamp === "number" ? { timestamp: activity.timestamp } : {}),
        },
      ]
    }
  )
  return {
    toolCalls,
    activities,
    assistantFiles: Array.from(
      new Set([
        ...sanitizeAssistantFiles(message.assistant_files),
        ...toolCalls.flatMap((toolCall) => toolCall.assistantFiles),
      ])
    ),
    ...(typeof message.input_item_id === "string" && message.input_item_id
      ? { inputItemId: message.input_item_id }
      : {}),
  }
}

export function useRealtimeAudio(options: UseRealtimeAudioOptions = {}) {
  const { sessionId, sessionType, callbacks, engine = "realtime" } = options
  const { selectedVoice } = useVoiceSettings()
  const sessionIdRef = useRef(sessionId)
  const callbacksRef = useRef(callbacks)
  const selectedVoiceRef = useRef(selectedVoice)
  const sessionTypeRef = useRef(sessionType)
  const engineRef = useRef<VoiceEngine>(engine)
  sessionIdRef.current = sessionId
  callbacksRef.current = callbacks
  selectedVoiceRef.current = selectedVoice
  sessionTypeRef.current = sessionType
  engineRef.current = engine

  const [state, setState] = useState<RealtimeAudioState>("idle")
  const [userTranscript, setUserTranscript] = useState<string>("")
  const [assistantTranscript, setAssistantTranscript] = useState<string>("")
  const [currentTool, setCurrentTool] = useState<string | null>(null)
  const [toolProgress, setToolProgress] = useState<VoiceToolProgress | null>(null)
  const [voiceActivities, setVoiceActivities] = useState<ActivityMessage[]>([])
  // The in-session transcript the voice window renders (see voice-transcript.ts).
  const [voiceTranscript, setVoiceTranscript] = useState<VoiceTranscriptEntry[]>([])
  const voiceTranscriptRef = useRef<VoiceTranscriptEntry[]>([])
  const pushTranscript = useCallback((event: VoiceTranscriptEvent) => {
    const next = reduceVoiceTranscript(voiceTranscriptRef.current, event, Date.now())
    if (next === voiceTranscriptRef.current) return
    voiceTranscriptRef.current = next
    setVoiceTranscript(next)
  }, [])
  // When the call became ready; the window's clock and transcript stamps count from here.
  const [sessionStartedAt, setSessionStartedAt] = useState<number | null>(null)
  // The caller hung up and the Live route is still closing and saving.
  const [isHangingUp, setIsHangingUp] = useState(false)
  const hangingUpRef = useRef(false)
  useEffect(() => {
    if (state === "listening" || state === "user_speaking") {
      setSessionStartedAt((current) => current ?? Date.now())
    }
  }, [state])
  const [isPlayingAudio, setIsPlayingAudio] = useState<boolean>(false)
  const [voiceToolApprovals, setVoiceToolApprovals] = useState<RealtimeVoiceToolApproval[]>([])
  const [isVoiceToolApprovalSubmitting, setIsVoiceToolApprovalSubmitting] = useState(false)

  // Refs for WebSocket and audio
  const wsRef = useRef<WebSocket | null>(null)
  const audioContextRef = useRef<AudioContext | null>(null)
  const mediaStreamRef = useRef<MediaStream | null>(null)
  const workletNodeRef = useRef<AudioWorkletNode | null>(null)
  const sourceNodeRef = useRef<MediaStreamAudioSourceNode | null>(null)
  const currentAssistantTranscriptRef = useRef<string>("")
  const voiceToolApprovalsRef = useRef<RealtimeVoiceToolApproval[]>([])
  const voiceToolApprovalSubmittingRef = useRef<string | null>(null)
  const activeBackgroundJobsRef = useRef(new Map<string, ActiveBackgroundJob>())
  const toolProgressRef = useRef<VoiceToolProgress | null>(null)
  const restoreBackgroundToolStateRef = useRef<() => boolean>(() => false)

  // Analyser node for waveform visualization
  const analyserNodeRef = useRef<AnalyserNode | null>(null)

  // Audio playback queue for smooth streaming
  const audioQueueRef = useRef<QueuedAudioChunk[]>([])
  const isPlayingRef = useRef<boolean>(false)
  const currentSourceRef = useRef<AudioBufferSourceNode | null>(null)

  // Pre-buffer by media duration because provider chunk sizes are not stable.
  const isBufferingRef = useRef<boolean>(true)
  const bufferTargetSecondsRef = useRef(VOICE_AUDIO_INITIAL_BUFFER_SECONDS)
  const bufferUnderrunCountRef = useRef(0)

  // Scheduled playback: track when next chunk should start (in AudioContext time)
  const nextPlayTimeRef = useRef<number>(0)

  // Track ALL scheduled sources so we can stop them all
  const scheduledSourcesRef = useRef<AudioBufferSourceNode[]>([])
  const scheduledSegmentsRef = useRef<ScheduledAudioSegment[]>([])
  const completedPlaybackSecondsRef = useRef(new Map<string, number>())
  const scheduleAudioChunksRef = useRef<() => void>(() => undefined)

  // Track current response for truncation
  const currentResponseIdRef = useRef<string | null>(null)
  const currentResponseInputItemIdRef = useRef<string | null>(null)
  const currentItemIdRef = useRef<string | null>(null)
  const currentResponseInterruptedRef = useRef(false)
  const finalizedResponseIdRef = useRef<string | null>(null)
  const expectedSocketCloseRef = useRef<WebSocket | null>(null)
  const hasEndedRef = useRef(false)
  const finishVoiceModeRef = useRef<() => void>(() => undefined)
  const startVoiceModeRef = useRef<() => void>(() => undefined)
  const scheduleLiveReconnectRef = useRef<(source: RealtimeSessionFailureSource) => void>(
    () => undefined
  )
  const sendLiveHangupRef = useRef<(socket: WebSocket) => void>(() => undefined)
  const beginLiveHangupWaitRef = useRef<(socket: WebSocket, sendHangup: boolean) => void>(
    () => undefined
  )
  const endVoiceModeForSafetyRef = useRef<(reason: RealtimeAutoHangupReason) => void>(
    () => undefined
  )
  const inactivityTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const maximumDurationTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const toolStallTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const toolWatchdogTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // GPT-Live only. Live has no VAD events, no response objects and no
  // client-driven cancel, so barge-in, delegation and reconnect are tracked here.
  const liveReconnectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const liveInterruptedTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const liveHangupTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  /** One automatic retry per voice session, never reset by a successful reconnect. */
  const liveReconnectUsedRef = useRef(false)
  /** True while the pending `startVoiceMode` call is the automatic retry. */
  const liveReconnectPendingRef = useRef(false)
  const liveUsageWarnedRef = useRef(false)
  /**
   * Deadline until which the `interrupted` display is held. It is deliberately
   * separate from `currentResponseInterruptedRef`: on Live the interrupted
   * response still closes as completed, so the turn stays valid while only the
   * on-screen state is pinned.
   */
  const liveInterruptedUntilRef = useRef(0)
  const liveActiveDelegationsRef = useRef(new Set<string>())
  const liveUserSpeakingRef = useRef(false)
  // The input item of the Live answer in progress. It outlives the segment
  // boundary (`response_done` clears the per-response ref) and ends with the
  // answer: its completion, the next committed caller turn, or teardown.
  const liveAnswerInputItemIdRef = useRef<string | null>(null)
  const liveUserTranscriptRef = useRef("")
  /**
   * The chat session the backend minted for this call. `sessionIdRef` only
   * catches up on the parent's next render, so a reconnect that races that
   * render would otherwise start a second chat session.
   */
  const liveBackendSessionIdRef = useRef<string | null>(null)

  const clearInactivityTimer = useCallback(() => {
    if (inactivityTimerRef.current !== null) {
      clearTimeout(inactivityTimerRef.current)
      inactivityTimerRef.current = null
    }
  }, [])

  const clearToolWatchdog = useCallback(() => {
    if (toolStallTimerRef.current !== null) {
      clearTimeout(toolStallTimerRef.current)
      toolStallTimerRef.current = null
    }
    if (toolWatchdogTimerRef.current !== null) {
      clearTimeout(toolWatchdogTimerRef.current)
      toolWatchdogTimerRef.current = null
    }
  }, [])

  const clearSafetyTimers = useCallback(() => {
    clearInactivityTimer()
    clearToolWatchdog()
    if (maximumDurationTimerRef.current !== null) {
      clearTimeout(maximumDurationTimerRef.current)
      maximumDurationTimerRef.current = null
    }
  }, [clearInactivityTimer, clearToolWatchdog])

  const clearLiveTimers = useCallback(() => {
    if (liveReconnectTimerRef.current !== null) {
      clearTimeout(liveReconnectTimerRef.current)
      liveReconnectTimerRef.current = null
    }
    if (liveInterruptedTimerRef.current !== null) {
      clearTimeout(liveInterruptedTimerRef.current)
      liveInterruptedTimerRef.current = null
    }
    if (liveHangupTimerRef.current !== null) {
      clearTimeout(liveHangupTimerRef.current)
      liveHangupTimerRef.current = null
    }
  }, [])

  const armMeaningfulActivityTimeout = useCallback(() => {
    clearInactivityTimer()
    inactivityTimerRef.current = setTimeout(() => {
      endVoiceModeForSafetyRef.current("inactivity")
    }, VOICE_INACTIVITY_TIMEOUT_MS)
  }, [clearInactivityTimer])
  const armMeaningfulActivityTimeoutRef = useRef(armMeaningfulActivityTimeout)
  armMeaningfulActivityTimeoutRef.current = armMeaningfulActivityTimeout

  const startSafetyTimers = useCallback(() => {
    clearSafetyTimers()
    armMeaningfulActivityTimeout()
    maximumDurationTimerRef.current = setTimeout(() => {
      endVoiceModeForSafetyRef.current("maximum_duration")
    }, VOICE_MAX_SESSION_DURATION_MS)
  }, [armMeaningfulActivityTimeout, clearSafetyTimers])
  const startSafetyTimersRef = useRef(startSafetyTimers)
  startSafetyTimersRef.current = startSafetyTimers

  /**
   * Re-arm the quiet-listening clock only when nothing is still working. On
   * Live an interrupted turn never reaches the `response_done` branch that
   * normally restarts it, so barge-in and delegation have to do it here or
   * the safety hang-up would silently stop working for the rest of the call.
   */
  const resumeInactivityTimeoutIfIdle = useCallback(() => {
    if (
      liveActiveDelegationsRef.current.size > 0 ||
      activeBackgroundJobsRef.current.size > 0 ||
      toolProgressRef.current != null ||
      voiceToolApprovalsRef.current.some((approval) => approval.status === "pending")
    ) {
      return
    }
    armMeaningfulActivityTimeoutRef.current()
  }, [])
  const resumeInactivityTimeoutIfIdleRef = useRef(resumeInactivityTimeoutIfIdle)
  resumeInactivityTimeoutIfIdleRef.current = resumeInactivityTimeoutIfIdle

  /**
   * True while the GPT-Live barge-in display owns the surface, so a normal
   * response close does not blink past the `interrupted` state.
   */
  const isLiveInterruptedDisplayHeld = useCallback(
    (current: RealtimeAudioState): boolean =>
      engineRef.current === "live" &&
      current === "interrupted" &&
      Date.now() < liveInterruptedUntilRef.current,
    []
  )

  /** Leave the tool state and let the session carry on listening. */
  const releaseToolState = useCallback(() => {
    clearToolWatchdog()
    toolProgressRef.current = null
    setToolProgress(null)
    setCurrentTool(null)
  }, [clearToolWatchdog])
  const releaseToolStateRef = useRef(releaseToolState)
  releaseToolStateRef.current = releaseToolState

  /**
   * Arm the client-side backstop for one tool call. The backend now always
   * sends a terminal frame, so reaching the watchdog means the frame never
   * arrived — a dead worker or a half-open socket. Recover to listening
   * instead of leaving the ring spinning on a call that will never report.
   */
  const armToolWatchdog = useCallback(
    (budgetSeconds: number | null, backgroundJobId?: string, watchdogOverrideMs?: number) => {
      clearToolWatchdog()
      toolStallTimerRef.current = setTimeout(() => {
        setToolProgress((current) => (current ? { ...current, stalled: true } : current))
      }, VOICE_TOOL_STALL_NOTICE_MS)

      const watchdogMs = Math.max(
        0,
        watchdogOverrideMs ??
          (budgetSeconds !== null && Number.isFinite(budgetSeconds) && budgetSeconds > 0
            ? budgetSeconds * 1000 + VOICE_TOOL_WATCHDOG_GRACE_MS
            : VOICE_TOOL_WATCHDOG_FALLBACK_MS)
      )
      toolWatchdogTimerRef.current = setTimeout(() => {
        logger.warn("Realtime tool call exceeded its client watchdog", { watchdogMs })
        if (backgroundJobId) activeBackgroundJobsRef.current.delete(backgroundJobId)
        if (!restoreBackgroundToolStateRef.current()) {
          releaseToolState()
          // The step never reported back; the record must not show it running
          // (and the window "Searching") for the rest of the call.
          pushTranscript({ type: "work_settle" })
          setState((current) => (current === "using_tools" ? "listening" : current))
        }
        armMeaningfulActivityTimeoutRef.current()
      }, watchdogMs)
    },
    [clearToolWatchdog, pushTranscript, releaseToolState]
  )
  const armToolWatchdogRef = useRef(armToolWatchdog)
  armToolWatchdogRef.current = armToolWatchdog

  /** Restore the newest detached job after a response or sibling tool ends. */
  const restoreBackgroundToolState = useCallback(() => {
    const remaining = Array.from(activeBackgroundJobsRef.current.entries()).at(-1)
    if (!remaining) return false

    const [jobId, job] = remaining
    setCurrentTool(job.name)
    const restoredProgress: VoiceToolProgress = {
      name: job.name,
      callId: jobId,
      startedAt: job.startedAt,
      stalled: Date.now() - job.startedAt >= VOICE_TOOL_STALL_NOTICE_MS,
    }
    toolProgressRef.current = restoredProgress
    setToolProgress(restoredProgress)
    armToolWatchdogRef.current(null, jobId, job.watchdogDeadline - Date.now())
    setState("using_tools")
    return true
  }, [])
  restoreBackgroundToolStateRef.current = restoreBackgroundToolState

  /**
   * Append one activity line to the overlay feed. The nested chat agent and
   * the direct tool path both narrate their work; this keeps only the tail of
   * the current turn so the surface stays glanceable rather than a log.
   */
  const pushVoiceActivity = useCallback((entry: ActivityMessage) => {
    setVoiceActivities((current) => {
      const previous = current.at(-1)
      if (
        previous &&
        previous.activity_type === entry.activity_type &&
        previous.message === entry.message
      ) {
        return current
      }
      return [...current, entry].slice(-VOICE_ACTIVITY_FEED_LIMIT)
    })
  }, [])
  const pushVoiceActivityRef = useRef(pushVoiceActivity)
  pushVoiceActivityRef.current = pushVoiceActivity

  // Initialize Audio Context and Analyser Node
  const initAudioContext = useCallback(() => {
    if (!audioContextRef.current) {
      audioContextRef.current = new AudioContext({ sampleRate: 24000 })
    }

    const ctx = audioContextRef.current
    if (ctx.state === "suspended") {
      void ctx.resume()
    }

    // Create analyser node for waveform visualization if not already created
    if (!analyserNodeRef.current) {
      analyserNodeRef.current = ctx.createAnalyser()
      analyserNodeRef.current.fftSize = 256
      analyserNodeRef.current.smoothingTimeConstant = 0.8
    }

    return ctx
  }, [])

  // Schedule and play audio chunks with precise timing
  // Uses AudioContext.currentTime for gapless playback independent of main thread
  const scheduleAudioChunks = useCallback(() => {
    const ctx = audioContextRef.current
    if (!ctx || audioQueueRef.current.length === 0) {
      return
    }

    // Schedule all available chunks
    while (audioQueueRef.current.length > 0) {
      const queuedChunk = audioQueueRef.current.shift()!
      const { buffer: audioBuffer, responseId } = queuedChunk

      // If we've fallen behind (or first chunk), reset to current time + small buffer
      if (nextPlayTimeRef.current < ctx.currentTime) {
        nextPlayTimeRef.current = ctx.currentTime + 0.01 // 10ms buffer
      }

      const source = ctx.createBufferSource()
      source.buffer = audioBuffer
      source.connect(ctx.destination)

      // Also connect to analyser for AI audio visualization
      if (analyserNodeRef.current) {
        source.connect(analyserNodeRef.current)
      }

      const scheduledStart = nextPlayTimeRef.current
      source.start(scheduledStart)
      // Track this source so we can stop it later
      scheduledSourcesRef.current.push(source)
      const segment: ScheduledAudioSegment = {
        source,
        responseId,
        start: scheduledStart,
        end: scheduledStart + audioBuffer.duration,
      }
      scheduledSegmentsRef.current.push(segment)

      // Remove from tracking array when it finishes playing
      source.onended = () => {
        const idx = scheduledSourcesRef.current.indexOf(source)
        if (idx > -1) {
          scheduledSourcesRef.current.splice(idx, 1)
        }
        const segmentIndex = scheduledSegmentsRef.current.indexOf(segment)
        if (segmentIndex > -1) {
          const heardUntil = Math.min(ctx.currentTime, segment.end)
          const heardSeconds = Math.max(0, heardUntil - segment.start)
          completedPlaybackSecondsRef.current.set(
            responseId,
            (completedPlaybackSecondsRef.current.get(responseId) ?? 0) + heardSeconds
          )
          scheduledSegmentsRef.current.splice(segmentIndex, 1)
        }
        const responseStillScheduled = scheduledSegmentsRef.current.some(
          (candidate) => candidate.responseId === responseId
        )
        const responseStillQueued = audioQueueRef.current.some(
          (candidate) => candidate.responseId === responseId
        )
        if (
          responseId !== currentResponseIdRef.current &&
          !responseStillScheduled &&
          !responseStillQueued
        ) {
          completedPlaybackSecondsRef.current.delete(responseId)
        }

        // Check if all scheduled audio has finished. A successor response may
        // already be prebuffering; it must not be appended to the predecessor.
        if (scheduledSourcesRef.current.length === 0) {
          isPlayingRef.current = false
          setIsPlayingAudio(false)
          const nextResponseId = audioQueueRef.current[0]?.responseId
          const bufferedSeconds = audioQueueRef.current.reduce(
            (total, queuedChunk) =>
              queuedChunk.responseId === nextResponseId
                ? total + queuedChunk.buffer.duration
                : total,
            0
          )
          const canStartBufferedResponse =
            bufferedSeconds + Number.EPSILON >= bufferTargetSecondsRef.current ||
            finalizedResponseIdRef.current === nextResponseId
          if (canStartBufferedResponse) {
            isBufferingRef.current = false
            isPlayingRef.current = true
            setIsPlayingAudio(true)
            scheduleAudioChunksRef.current()
          } else {
            isBufferingRef.current = true
            if (
              responseId === currentResponseIdRef.current &&
              finalizedResponseIdRef.current !== responseId
            ) {
              bufferUnderrunCountRef.current += 1
              bufferTargetSecondsRef.current = Math.min(
                VOICE_AUDIO_MAX_BUFFER_SECONDS,
                bufferTargetSecondsRef.current + VOICE_AUDIO_REBUFFER_STEP_SECONDS
              )
              logger.warn("Realtime audio buffer underrun", {
                underrunCount: bufferUnderrunCountRef.current,
                targetBufferMs: Math.round(bufferTargetSecondsRef.current * 1000),
              })
            }
          }
        }
      }

      // Update next play time to be after this chunk finishes
      nextPlayTimeRef.current += audioBuffer.duration
      // Keep track of the last source for stopping on interrupt
      currentSourceRef.current = source
    }
  }, [])
  scheduleAudioChunksRef.current = scheduleAudioChunks

  // Legacy function name for compatibility - now just triggers scheduling
  const playNextInQueue = useCallback(() => {
    if (audioQueueRef.current.length === 0) {
      isPlayingRef.current = false
      setIsPlayingAudio(false)
      // Reset to buffering mode when queue empties (for next response)
      isBufferingRef.current = true
      return
    }

    isPlayingRef.current = true
    setIsPlayingAudio(true)
    scheduleAudioChunks()
  }, [scheduleAudioChunks])

  // Queue audio chunk for playback
  const queueAudioChunk = useCallback(
    (arrayBuffer: ArrayBuffer) => {
      const ctx = initAudioContext()

      // PCM16 to Float32
      const int16Array = new Int16Array(arrayBuffer)
      const float32Array = new Float32Array(int16Array.length)
      for (let i = 0; i < int16Array.length; i++) {
        float32Array[i] = int16Array[i] / 32768
      }

      const audioBuffer = ctx.createBuffer(1, float32Array.length, 24000)
      audioBuffer.copyToChannel(float32Array, 0)

      const responseId = currentResponseIdRef.current
      if (!responseId || currentResponseInterruptedRef.current) return

      audioQueueRef.current.push({ buffer: audioBuffer, responseId })

      // Start only after enough media time is buffered. Counting chunks makes
      // jitter tolerance vary with provider packetization.
      if (!isPlayingRef.current) {
        if (isBufferingRef.current) {
          const bufferedSeconds = audioQueueRef.current.reduce(
            (total, queuedChunk) =>
              queuedChunk.responseId === responseId ? total + queuedChunk.buffer.duration : total,
            0
          )
          if (bufferedSeconds + Number.EPSILON >= bufferTargetSecondsRef.current) {
            logger.debug("Realtime audio duration buffer filled", {
              bufferedMs: Math.round(bufferedSeconds * 1000),
              targetBufferMs: Math.round(bufferTargetSecondsRef.current * 1000),
            })
            isBufferingRef.current = false
            playNextInQueue()
          }
        } else {
          // Not in buffering mode, play immediately
          playNextInQueue()
        }
      } else if (!isBufferingRef.current) {
        // Already playing - schedule new chunks immediately for gapless playback
        scheduleAudioChunks()
      }
    },
    [initAudioContext, playNextInQueue, scheduleAudioChunks]
  )

  // Stop audio playback immediately (for interruption)
  const stopAudioPlayback = useCallback(() => {
    const scheduledSources = [...scheduledSourcesRef.current]
    // Detach ownership before stop() can deliver onended. A stopped callback
    // must never start buffered audio or account duration a second time.
    scheduledSourcesRef.current = []
    scheduledSegmentsRef.current = []
    audioQueueRef.current = []
    completedPlaybackSecondsRef.current.clear()

    // Stop ALL scheduled sources (not just current)
    for (const source of scheduledSources) {
      try {
        source.stop()
      } catch {
        // Already stopped
      }
    }
    currentSourceRef.current = null

    isPlayingRef.current = false
    setIsPlayingAudio(false)
    // Reset buffering state for next response
    isBufferingRef.current = true
    bufferTargetSecondsRef.current = VOICE_AUDIO_INITIAL_BUFFER_SECONDS
    bufferUnderrunCountRef.current = 0
    // Reset scheduled playback time
    nextPlayTimeRef.current = 0
  }, [])

  /**
   * Hold the overlay on `interrupted` just long enough to read, then hand the
   * surface back to the microphone states. Live never sends `speech_started`,
   * so an uncommitted user transcript delta is the only evidence that the user
   * is still talking over the model.
   */
  const scheduleLiveInterruptedReset = useCallback(() => {
    if (liveInterruptedTimerRef.current !== null) clearTimeout(liveInterruptedTimerRef.current)
    liveInterruptedTimerRef.current = setTimeout(() => {
      liveInterruptedTimerRef.current = null
      liveInterruptedUntilRef.current = 0
      // Held in an object so the updater's write survives control-flow analysis.
      const transition = { leftInterruptedState: false }
      setState((current) => {
        if (current !== "interrupted") return current
        transition.leftInterruptedState = true
        return liveUserSpeakingRef.current ? "user_speaking" : "listening"
      })
      // The response that was spoken over closes normally and rearms the clock
      // itself. Only a barge-in that never saw that close needs this backstop.
      if (transition.leftInterruptedState) resumeInactivityTimeoutIfIdleRef.current()
    }, LIVE_INTERRUPTED_STATE_MS)
  }, [])
  const scheduleLiveInterruptedResetRef = useRef(scheduleLiveInterruptedReset)
  scheduleLiveInterruptedResetRef.current = scheduleLiveInterruptedReset

  // A non-completed response must not leave audio behind. Remove only the
  // matching response so an older response tail (or a newer response) is not
  // stopped by a late terminal event.
  const discardResponseAudio = useCallback((responseId: string) => {
    audioQueueRef.current = audioQueueRef.current.filter(
      (queuedChunk) => queuedChunk.responseId !== responseId
    )

    const discardedSources = new Set(
      scheduledSegmentsRef.current
        .filter((segment) => segment.responseId === responseId)
        .map((segment) => segment.source)
    )
    scheduledSegmentsRef.current = scheduledSegmentsRef.current.filter(
      (segment) => segment.responseId !== responseId
    )
    scheduledSourcesRef.current = scheduledSourcesRef.current.filter(
      (source) => !discardedSources.has(source)
    )
    completedPlaybackSecondsRef.current.delete(responseId)

    for (const source of discardedSources) {
      try {
        source.stop()
      } catch {
        // Already stopped
      }
    }

    if (scheduledSourcesRef.current.length === 0) {
      currentSourceRef.current = null
      isPlayingRef.current = false
      setIsPlayingAudio(false)
      isBufferingRef.current = true
      nextPlayTimeRef.current = 0
      return
    }

    currentSourceRef.current = scheduledSourcesRef.current.at(-1) ?? null
    nextPlayTimeRef.current = Math.max(
      audioContextRef.current?.currentTime ?? 0,
      ...scheduledSegmentsRef.current.map((segment) => segment.end)
    )
  }, [])

  const discardActiveUnverifiedTurn = useCallback(
    (source: RealtimeSessionFailureSource) => {
      const responseId = currentResponseIdRef.current
      const inputItemId = currentResponseInputItemIdRef.current
      if (responseId) discardResponseAudio(responseId)

      currentResponseInterruptedRef.current = true
      finalizedResponseIdRef.current = null
      currentAssistantTranscriptRef.current = ""
      setUserTranscript("")
      setAssistantTranscript("")
      releaseToolStateRef.current()
      callbacksRef.current?.onInterrupt?.({
        reason: "session_failed",
        source,
        ...(responseId ? { responseId } : {}),
        ...(inputItemId ? { inputItemId } : {}),
      })
    },
    [discardResponseAudio]
  )

  // Send truncation request to stop AI mid-response
  const truncateResponse = useCallback(() => {
    if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) return
    // GPT-Live owns barge-in upstream and rejects truncation. Live never emits
    // the `speech_started` frame that reaches here, but fail closed anyway.
    if (engineRef.current === "live") return
    if (!currentItemIdRef.current) return

    const responseId = currentResponseIdRef.current
    const ctx = audioContextRef.current
    const completedSeconds = responseId
      ? (completedPlaybackSecondsRef.current.get(responseId) ?? 0)
      : 0
    const activeSeconds =
      responseId && ctx
        ? scheduledSegmentsRef.current
            .filter((segment) => segment.responseId === responseId)
            .reduce((total, segment) => {
              const heardUntil = Math.min(ctx.currentTime, segment.end)
              return total + Math.max(0, heardUntil - segment.start)
            }, 0)
        : 0
    const heardSeconds = completedSeconds + activeSeconds
    const truncateMsg = {
      type: "conversation.item.truncate",
      item_id: currentItemIdRef.current,
      content_index: 0,
      audio_end_ms: Math.round(heardSeconds * 1000),
    }

    wsRef.current.send(JSON.stringify(truncateMsg))
    logger.info("Sent truncation request", { audioEndMs: truncateMsg.audio_end_ms })

    // Keep the interrupted response id until response.created replaces it so
    // late tagged frames remain attributable to the cancelled response.
    currentItemIdRef.current = null
  }, [])

  const isActiveResponse = useCallback((responseId: string | null): boolean => {
    if (
      currentResponseInterruptedRef.current ||
      currentResponseIdRef.current === null ||
      responseId === null
    ) {
      return false
    }
    return responseId === currentResponseIdRef.current
  }, [])

  const isCompletableResponse = useCallback((responseId: string | null): boolean => {
    if (currentResponseInterruptedRef.current || responseId === null) return false
    if (responseId === currentResponseIdRef.current) return true
    // Azure can deliver response.done immediately before the final transcript.
    // Accept that completed response only while no successor is active;
    // unsuccessful responses clear finalizedResponseIdRef instead.
    return currentResponseIdRef.current === null && responseId === finalizedResponseIdRef.current
  }, [])

  // Handle incoming WebSocket messages
  const handleMessage = useCallback(
    (event: MessageEvent) => {
      const data = event.data

      if (data instanceof ArrayBuffer) {
        // The route keeps forwarding audio until the provider confirms the
        // close; after a hang-up none of it may be heard.
        if (hangingUpRef.current) return
        if (!isActiveResponse(currentResponseIdRef.current)) return
        queueAudioChunk(data)
        return
      }

      try {
        const msg = JSON.parse(data)

        switch (msg.type) {
          case "voice_tool_approval": {
            const approval = normalizeVoiceToolApproval(msg)
            if (!approval) {
              logger.warn("Voice mode: Ignored malformed tool approval frame")
              break
            }

            const approvalKey = `${approval.sessionId}:${approval.request_id}:${approval.call_id}`
            const matchesApproval = (candidate: RealtimeVoiceToolApproval) =>
              candidate.sessionId === approval.sessionId &&
              candidate.request_id === approval.request_id &&
              candidate.call_id === approval.call_id

            if (approval.status === "pending") {
              const currentApprovals = voiceToolApprovalsRef.current
              const existingIndex = currentApprovals.findIndex(matchesApproval)
              const nextApprovals = [...currentApprovals]
              if (existingIndex >= 0) nextApprovals[existingIndex] = approval
              else nextApprovals.push(approval)
              voiceToolApprovalsRef.current = nextApprovals
              setVoiceToolApprovals(nextApprovals)
              clearInactivityTimer()
              setState("using_tools")
              logger.info("Voice mode: Tool approval requested", {
                tool: approval.tool,
                requestId: approval.request_id,
              })
              break
            }

            // Remove only the exact request resolved by the authoritative
            // update. Other pending calls retain their original queue order.
            const nextApprovals = voiceToolApprovalsRef.current.filter(
              (candidate) => !matchesApproval(candidate)
            )
            if (nextApprovals.length !== voiceToolApprovalsRef.current.length) {
              voiceToolApprovalsRef.current = nextApprovals
              setVoiceToolApprovals(nextApprovals)
              if (voiceToolApprovalSubmittingRef.current === approvalKey) {
                voiceToolApprovalSubmittingRef.current = null
                setIsVoiceToolApprovalSubmitting(false)
              }
              logger.info("Voice mode: Tool approval resolved", {
                tool: approval.tool,
                requestId: approval.request_id,
                status: approval.status,
              })
            }
            break
          }

          case "voice_tool_approval_decision_rejected": {
            const sessionId = typeof msg.session_id === "string" ? msg.session_id : ""
            const requestId = typeof msg.request_id === "string" ? msg.request_id : ""
            const callId = typeof msg.call_id === "string" ? msg.call_id : ""
            const rejectedKey = `${sessionId}:${requestId}:${callId}`
            const approvalStillPending = voiceToolApprovalsRef.current.some(
              (approval) =>
                approval.sessionId === sessionId &&
                approval.request_id === requestId &&
                approval.call_id === callId
            )
            if (approvalStillPending && voiceToolApprovalSubmittingRef.current === rejectedKey) {
              voiceToolApprovalSubmittingRef.current = null
              setIsVoiceToolApprovalSubmitting(false)
              logger.warn("Voice mode: Tool approval decision was rejected", {
                requestId,
                reason: typeof msg.reason === "string" ? msg.reason : "unknown",
              })
            }
            break
          }

          // Speech detection - user started speaking
          case "speech_started":
          case "input_audio_buffer.speech_started": {
            setState("user_speaking")
            // A response can be active before the duration buffer starts.
            // Its scheduled tail can also remain audible after response.done.
            const hasInterruptibleOutput =
              currentResponseIdRef.current !== null || isPlayingRef.current
            if (hasInterruptibleOutput && !currentResponseInterruptedRef.current) {
              const interruptedResponseId = currentResponseIdRef.current
              currentResponseInterruptedRef.current = true
              truncateResponse()
              stopAudioPlayback()
              pushTranscript({ type: "assistant_interrupted" })
              callbacksRef.current?.onInterrupt?.({
                reason: "barge_in",
                ...(interruptedResponseId ? { responseId: interruptedResponseId } : {}),
              })
            }
            break
          }

          // Speech detection - user stopped speaking
          case "speech_stopped":
          case "input_audio_buffer.speech_stopped":
            // Realtime answers every committed turn, so "thinking" always ends.
            // GPT-Live may let a backchannel pass without an answer; entering
            // `processing` there would leave the surface thinking forever.
            if (engineRef.current === "live") {
              setState((current) => (current === "user_speaking" ? "listening" : current))
              break
            }
            setState("processing")
            break

          // User's speech transcribed
          // The backend emits this proxy event before forwarding the matching
          // raw Azure transcript event. Use the proxy as the sole transcript
          // source so one utterance cannot enter persistence twice.
          case "user_transcript": {
            const userText = msg.text || msg.transcript || ""
            // The committed turn supersedes any Live speech-in-progress text.
            liveUserSpeakingRef.current = false
            liveUserTranscriptRef.current = ""
            if (typeof userText === "string" && userText.trim()) {
              // VAD speech_started can be road/wind noise. Only a confirmed,
              // meaningful transcript pauses the quiet-listening clock while
              // the assistant and its chat-agent tools do real work.
              clearInactivityTimer()
              // No answer is promised on Live. A committed turn ends the answer
              // before it, and with nothing open the clock restarts from this
              // turn; `response.created` stops it again the moment the model
              // does answer.
              const isLiveTurn = engineRef.current === "live"
              if (isLiveTurn) liveAnswerInputItemIdRef.current = null
              if (isLiveTurn && currentResponseIdRef.current === null) {
                resumeInactivityTimeoutIfIdleRef.current()
              }
              // The feed describes the turn in flight, so a new user utterance
              // starts it over rather than stacking onto the previous answer.
              setVoiceActivities([])
              setUserTranscript(userText)
              pushTranscript({ type: "user_committed", text: userText })
              const inputItemId = typeof msg.item_id === "string" ? msg.item_id : undefined
              if (inputItemId) {
                callbacksRef.current?.onUserTranscript?.(userText, inputItemId)
              } else {
                callbacksRef.current?.onUserTranscript?.(userText)
              }
              logger.info("Voice user transcript received", { characterCount: userText.length })
            }
            break
          }

          // AI response started
          case "response.created": {
            // Never carry unscheduled audio from an older response into its
            // successor. Scheduled predecessor audio may finish naturally,
            // while the successor builds its own prebuffer.
            audioQueueRef.current = []
            isBufferingRef.current = true
            bufferTargetSecondsRef.current = VOICE_AUDIO_INITIAL_BUFFER_SECONDS
            bufferUnderrunCountRef.current = 0
            // Live may split one spoken answer into several synthetic responses
            // when the model pauses. Every segment of an answer carries the same
            // input item, so the caption has to survive the segment boundary
            // instead of restarting from the next delta.
            const segmentInputItemId = readInputItemId(msg)
            // Compared with the ANSWER's item, not the previous response's:
            // the route always closes a segment (`response_done`, which clears
            // the per-response ref) before it opens the next one.
            const continuesLiveAnswer =
              engineRef.current === "live" &&
              segmentInputItemId !== null &&
              segmentInputItemId === liveAnswerInputItemIdRef.current
            if (engineRef.current === "live") {
              liveAnswerInputItemIdRef.current = segmentInputItemId
            }
            currentResponseIdRef.current = msg.response?.id || null
            currentResponseInputItemIdRef.current = continuesLiveAnswer ? segmentInputItemId : null
            currentItemIdRef.current = null
            currentResponseInterruptedRef.current = false
            if (!continuesLiveAnswer) {
              currentAssistantTranscriptRef.current = ""
            }
            clearInactivityTimer()
            setState("ai_speaking")
            break
          }

          // AI audio transcript delta
          case "assistant_transcript_delta": {
            if (!isActiveResponse(msg.response_id ?? msg.response?.id ?? null)) break
            const delta = msg.text || msg.delta || ""
            if (delta) {
              currentAssistantTranscriptRef.current += delta
              setAssistantTranscript(currentAssistantTranscriptRef.current)
              pushTranscript({ type: "assistant_delta", text: delta })
              const inputItemId =
                typeof msg.input_item_id === "string" ? msg.input_item_id : undefined
              if (inputItemId) {
                currentResponseInputItemIdRef.current = inputItemId
                callbacksRef.current?.onAssistantTranscriptDelta?.(delta, inputItemId)
              } else {
                callbacksRef.current?.onAssistantTranscriptDelta?.(delta)
              }
            }
            break
          }

          // AI audio transcript complete
          case "assistant_transcript_done": {
            if (!isCompletableResponse(msg.response_id ?? msg.response?.id ?? null)) break
            const fullText = msg.text || msg.transcript || currentAssistantTranscriptRef.current
            const visualText =
              typeof msg.visual_text === "string" && msg.visual_text ? msg.visual_text : fullText
            const completion = normalizeAssistantCompletion(msg)
            if (completion.inputItemId) {
              currentResponseInputItemIdRef.current = completion.inputItemId
            }
            if (engineRef.current === "live") {
              liveAnswerInputItemIdRef.current = null
              // The Live route stamps every answer to a caller turn with that
              // turn's item; none means the model spoke on its own.
              if (!completion.inputItemId) completion.unprompted = true
            }
            const hasCompletionMetadata = Boolean(
              completion.inputItemId ||
              completion.unprompted ||
              completion.toolCalls.length ||
              completion.activities.length ||
              completion.assistantFiles.length
            )
            currentAssistantTranscriptRef.current = ""
            pushTranscript({
              type: "assistant_done",
              text: typeof fullText === "string" ? fullText : "",
            })
            if (fullText) {
              setAssistantTranscript(fullText)
              logger.info("Voice assistant transcript completed", {
                characterCount: fullText.length,
              })
            }
            if (visualText || hasCompletionMetadata) {
              if (hasCompletionMetadata) {
                callbacksRef.current?.onAssistantTranscriptDone?.(visualText, completion)
              } else {
                callbacksRef.current?.onAssistantTranscriptDone?.(visualText)
              }
            }
            break
          }

          // Audio chunk from AI
          // The backend normalizes both preview and GA provider events into
          // audio_delta before forwarding the raw frame. Consuming the preview
          // alias here as well schedules every packet twice and sounds like a
          // model stutter.
          case "audio_delta": {
            if (hangingUpRef.current) break
            if (!isActiveResponse(msg.response_id ?? msg.response?.id ?? null)) break
            const audioData = msg.audio || msg.delta
            if (audioData) {
              // Track item for potential truncation
              if (msg.item_id) {
                currentItemIdRef.current = msg.item_id
              }
              // Decode base64 and queue
              const binaryString = atob(audioData)
              const bytes = new Uint8Array(binaryString.length)
              for (let i = 0; i < binaryString.length; i++) {
                bytes[i] = binaryString.charCodeAt(i)
              }
              queueAudioChunk(bytes.buffer)
              setState("ai_speaking")
            }
            break
          }

          // Response completed
          case "response_done":
          case "response.done": {
            const responseId = msg.response_id ?? msg.response?.id ?? null
            if (!isActiveResponse(responseId)) break
            const responseStatus = msg.status ?? msg.response?.status
            const isOutputDiscardHandoff =
              msg.discard_output === true &&
              (responseStatus === "completed" || msg.superseded === true)
            let unsuccessfulStatus: "cancelled" | "failed" | "incomplete" | null = null
            if (
              responseStatus === "cancelled" ||
              responseStatus === "failed" ||
              responseStatus === "incomplete"
            ) {
              unsuccessfulStatus = responseStatus
            } else if (msg.discard_output === true && !isOutputDiscardHandoff) {
              unsuccessfulStatus = "incomplete"
            }
            if (isOutputDiscardHandoff && currentResponseIdRef.current) {
              const discardedResponseId = currentResponseIdRef.current
              // A route-held native response streams no text, so the server
              // names its input item on the discard frame itself.
              const discardedInputItemId =
                currentResponseInputItemIdRef.current ??
                (typeof msg.input_item_id === "string" && msg.input_item_id
                  ? msg.input_item_id
                  : null)
              const preserveSpokenPreamble = msg.preserve_audio === true
              finalizedResponseIdRef.current = preserveSpokenPreamble ? discardedResponseId : null
              currentResponseInterruptedRef.current = !preserveSpokenPreamble
              if (preserveSpokenPreamble) {
                const queuedResponseId = audioQueueRef.current[0]?.responseId
                if (
                  queuedResponseId === discardedResponseId &&
                  scheduledSourcesRef.current.length === 0
                ) {
                  // A short tool preamble may finish streaming before the
                  // normal prebuffer threshold. Let it finish while the tool
                  // runs instead of cutting the sentence at handoff.
                  isBufferingRef.current = false
                  playNextInQueue()
                }
              } else {
                discardResponseAudio(discardedResponseId)
              }
              currentAssistantTranscriptRef.current = ""
              setAssistantTranscript("")
              callbacksRef.current?.onInterrupt?.({
                reason: "response_output_discarded",
                responseId: discardedResponseId,
                ...(discardedInputItemId ? { inputItemId: discardedInputItemId } : {}),
              })
              logger.info(
                preserveSpokenPreamble
                  ? "Preserved spoken preamble during realtime tool handoff"
                  : "Discarded provider output for realtime response handoff"
              )
            } else if (unsuccessfulStatus && currentResponseIdRef.current) {
              const discardedResponseId = currentResponseIdRef.current
              const discardedInputItemId = currentResponseInputItemIdRef.current
              finalizedResponseIdRef.current = null
              currentResponseInterruptedRef.current = true
              discardResponseAudio(discardedResponseId)
              currentAssistantTranscriptRef.current = ""
              setAssistantTranscript("")
              if (!restoreBackgroundToolStateRef.current()) {
                releaseToolStateRef.current()
              }
              callbacksRef.current?.onInterrupt?.({
                reason: "response_discarded",
                responseId: discardedResponseId,
                status: unsuccessfulStatus,
                ...(discardedInputItemId ? { inputItemId: discardedInputItemId } : {}),
              })
              logger.warn("Realtime response did not complete", { status: unsuccessfulStatus })
            } else {
              finalizedResponseIdRef.current = currentResponseIdRef.current
              const queuedResponseId = audioQueueRef.current[0]?.responseId
              if (
                queuedResponseId === currentResponseIdRef.current &&
                scheduledSourcesRef.current.length === 0
              ) {
                // The API may complete a short response before the normal
                // duration threshold. Flush only that response's pending
                // audio so it cannot leak into the next one.
                isBufferingRef.current = false
                playNextInQueue()
              }
            }
            // A detached read may still be running after this spoken response.
            const settledState =
              activeBackgroundJobsRef.current.size > 0 ? "using_tools" : "listening"
            // On Live the close of a response the user spoke over arrives
            // within the barge-in window. Let the `interrupted` display stand
            // until its own timer retires it, rather than blinking past it.
            setState((current) => (isLiveInterruptedDisplayHeld(current) ? current : settledState))
            const shouldHoldInactivity =
              msg.discard_output === true ||
              activeBackgroundJobsRef.current.size > 0 ||
              toolProgressRef.current != null ||
              voiceToolApprovalsRef.current.some((approval) => approval.status === "pending")
            if (shouldHoldInactivity) {
              clearInactivityTimer()
            } else {
              armMeaningfulActivityTimeoutRef.current()
            }
            currentResponseIdRef.current = null
            currentResponseInputItemIdRef.current = null
            currentItemIdRef.current = null
            break
          }

          // GPT-Live barge-in. The model stopped because the user spoke over
          // it. Unlike the Realtime `speech_started` path this must not
          // truncate (Live rejects it) and must not call `onInterrupt`:
          // history on Live is owned by committed turns, so routing this into
          // the barge-in branch would delete an exchange the user did hear.
          case "audio_interrupted": {
            const interruptedResponseId =
              typeof msg.response_id === "string" ? msg.response_id : currentResponseIdRef.current
            logger.info("Live voice output interrupted by the speaker")
            // Flush what is still queued, and nothing more. The backend closes
            // this response normally right after — `response_done{completed}`
            // plus the `assistant_transcript_done` carrying the heard text —
            // and both must still pass the active/completable guards so the
            // exchange reaches history. Marking the response interrupted here
            // would drop the very turn the user listened to.
            if (interruptedResponseId) discardResponseAudio(interruptedResponseId)
            else stopAudioPlayback()
            pushTranscript({ type: "assistant_interrupted" })
            liveInterruptedUntilRef.current = Date.now() + LIVE_INTERRUPTED_STATE_MS
            setState("interrupted")
            scheduleLiveInterruptedResetRef.current()
            break
          }

          // Per-second billing telemetry. Log only — the session limit is
          // enforced by the backend, which closes the socket when it is spent.
          case "live_usage": {
            const usageRatio = typeof msg.usage_ratio === "number" ? msg.usage_ratio : null
            const seconds = typeof msg.seconds === "number" ? msg.seconds : null
            logger.debug("Live voice usage", { seconds, usageRatio })
            if (
              usageRatio !== null &&
              usageRatio >= LIVE_USAGE_WARNING_RATIO &&
              !liveUsageWarnedRef.current
            ) {
              liveUsageWarnedRef.current = true
              logger.warn("Live voice session is near its usage limit", { usageRatio })
            }
            break
          }

          // Live runs tools through a Responses delegation rather than
          // in-session calls, so the delegation brackets the tool UI the same
          // way `tool_call_started`/`tool_completed` do on Realtime.
          case "live_delegation_started": {
            const delegationId = typeof msg.delegation_id === "string" ? msg.delegation_id : ""
            if (delegationId) liveActiveDelegationsRef.current.add(delegationId)
            clearInactivityTimer()
            setState("using_tools")
            break
          }

          case "live_delegation_finished": {
            const delegationId = typeof msg.delegation_id === "string" ? msg.delegation_id : ""
            if (delegationId) liveActiveDelegationsRef.current.delete(delegationId)
            logger.info("Live voice delegation finished", {
              status: typeof msg.status === "string" ? msg.status : "unknown",
            })
            if (liveActiveDelegationsRef.current.size > 0) break
            // A failed run drops the calls it never started without a terminal
            // frame; with no delegation left, nothing can still be running.
            pushTranscript({ type: "work_settle" })
            if (!restoreBackgroundToolStateRef.current()) {
              releaseToolStateRef.current()
              setState((current) => (current === "using_tools" ? "listening" : current))
            }
            resumeInactivityTimeoutIfIdleRef.current()
            break
          }

          // Uncommitted speech-in-progress text. It is not a turn, so it never
          // reaches `onUserTranscript`; it only feeds the overlay and tooltip.
          case "live_user_transcript_delta": {
            const deltaText = typeof msg.text === "string" ? msg.text : ""
            if (!deltaText) break
            liveUserSpeakingRef.current = true
            liveUserTranscriptRef.current += deltaText
            setUserTranscript(liveUserTranscriptRef.current)
            pushTranscript({ type: "user_live", text: liveUserTranscriptRef.current })
            // A barge-in display keeps the surface for its own window; the
            // reset timer then moves straight to `user_speaking` from here.
            setState((current) => (current === "listening" ? "user_speaking" : current))
            break
          }

          // The backend finished the session on purpose (hang-up, budget,
          // upstream close). Hang-up (`code` null / missing) stays silent; a
          // spent budget or a content filter uses the same overlay as a named
          // `{type:"error"}` frame. Never reconnect: persist already ran.
          case "live_session_closed": {
            const closedCode = typeof msg.code === "string" && msg.code ? msg.code : null
            logger.info("Live voice session closed", {
              reason: typeof msg.reason === "string" ? msg.reason : "unknown",
              code: closedCode ?? "none",
            })
            liveReconnectUsedRef.current = true
            if (closedCode && LIVE_SESSION_CLOSED_ERROR_CODES.has(closedCode)) {
              discardActiveUnverifiedTurn("provider_error")
              callbacksRef.current?.onError?.(closedCode as LiveVoiceError)
            }
            const closedSocket = wsRef.current
            if (closedSocket) {
              expectedSocketCloseRef.current = closedSocket
              closedSocket.onmessage = null
              closedSocket.close()
            }
            finishVoiceModeRef.current()
            break
          }

          // Mute is a backend-owned Live concept with no UI on this surface.
          case "voice_mute_state":
            break

          // The route finished its handshake. Structural only: never log the
          // voice, the session id or anything the user said. The client keeps
          // its own maximum-duration guard regardless of `expires_at`.
          case "voice_session_ready":
            logger.info("Voice mode: Session ready", {
              modelProfile: typeof msg.model_profile === "string" ? msg.model_profile : "unknown",
              deployment: typeof msg.deployment === "string" ? msg.deployment : "unknown",
              delegationMode:
                typeof msg.delegation_mode === "string" ? msg.delegation_mode : "unknown",
              voiceFallback: msg.voice_fallback === true,
            })
            break

          // Errors
          case "transcription_error": {
            // Azure documents this as an item-scoped ASR failure, separate
            // from session-level error events. The Realtime model has already
            // processed the audio, so keep the voice session alive and let the
            // persistence layer consume only this failed turn boundary.
            const failure: RealtimeTranscriptionFailure = {
              ...(typeof msg.code === "string" && msg.code ? { code: msg.code } : {}),
              ...(typeof msg.error_type === "string" && msg.error_type
                ? { errorType: msg.error_type }
                : {}),
              ...(typeof msg.item_id === "string" && msg.item_id ? { itemId: msg.item_id } : {}),
            }
            logger.warn("Realtime input transcription unavailable", {
              code: failure.code ?? "unknown",
              errorType: failure.errorType ?? "unknown",
            })
            callbacksRef.current?.onTranscriptionFailure?.(failure)
            if (currentResponseIdRef.current === null && !isPlayingRef.current) {
              setState("listening")
            }
            break
          }

          case "realtime_recoverable_error": {
            logger.warn("Recoverable Realtime provider event rejected", {
              code: typeof msg.code === "string" ? msg.code : "unknown",
              eventType: typeof msg.event_type === "string" ? msg.event_type : "unknown",
              purpose: typeof msg.purpose === "string" ? msg.purpose : "unknown",
            })
            const rejectedResponseId = typeof msg.response_id === "string" ? msg.response_id : null
            if (rejectedResponseId && currentResponseIdRef.current === rejectedResponseId) {
              const rejectedInputItemId = currentResponseInputItemIdRef.current
              discardResponseAudio(rejectedResponseId)
              currentResponseInterruptedRef.current = true
              finalizedResponseIdRef.current = null
              currentAssistantTranscriptRef.current = ""
              currentResponseIdRef.current = null
              currentResponseInputItemIdRef.current = null
              currentItemIdRef.current = null
              setAssistantTranscript("")
              callbacksRef.current?.onInterrupt?.({
                reason: "response_discarded",
                responseId: rejectedResponseId,
                status: "failed",
                ...(rejectedInputItemId ? { inputItemId: rejectedInputItemId } : {}),
              })
            }
            if (msg.event_type === "response.create") {
              if (activeBackgroundJobsRef.current.size === 0) {
                releaseToolStateRef.current()
              }
              setState(activeBackgroundJobsRef.current.size > 0 ? "using_tools" : "listening")
              armMeaningfulActivityTimeoutRef.current()
            }
            break
          }

          case "error": {
            // The Live route names its refusal with a code string; Realtime
            // sends an opaque object and keeps the generic service message.
            const liveCode =
              engineRef.current === "live" &&
              typeof msg.error === "string" &&
              LIVE_VOICE_ERRORS.has(msg.error)
                ? (msg.error as LiveVoiceError)
                : null
            if (
              liveCode === "live_connection_lost" &&
              !liveReconnectUsedRef.current &&
              !hangingUpRef.current
            ) {
              // The upstream link dropped. This is the one Live refusal the
              // single retry exists for, so recover instead of ending the call.
              logger.warn("Live voice lost its upstream connection; retrying once")
              const lostSocket = wsRef.current
              if (lostSocket) {
                lostSocket.onmessage = null
                lostSocket.onclose = null
                expectedSocketCloseRef.current = lostSocket
                lostSocket.close()
              }
              scheduleLiveReconnectRef.current("provider_error")
              break
            }
            // A fair-use refusal (`fair_use_blocked` / `plan_upgrade_required`)
            // rides this same frame. Read it BEFORE falling back to the generic
            // code so the overlay can name the real reason and the budget card
            // can open (spec invariant: a blocked or plan-locked feature never
            // produces a generic error).
            const fairUse = extractFairUseError(msg)
            if (fairUse) {
              logger.info(`Realtime session refused by fair use: ${fairUse.code}`)
              callbacksRef.current?.onFairUseRefusal?.(msg)
            } else if (liveCode) {
              // Every other named refusal is terminal — a spent budget, a
              // policy stop or a missing deployment all repeat on a retry.
              liveReconnectUsedRef.current = true
              logger.error("Live voice route refused the session", { code: liveCode })
            } else {
              logger.error("Realtime API error")
            }
            discardActiveUnverifiedTurn("provider_error")
            callbacksRef.current?.onError?.(fairUse ? fairUse.code : (liveCode ?? "service_error"))
            const failedSocket = wsRef.current
            // After a Live call has started, persist is still in flight. Keep
            // `onmessage` so `session_created` / `live_session_closed` can land;
            // the hang-up grace timer is the backstop. Fail closed on capture
            // without closing the socket.
            if (
              failedSocket &&
              engineRef.current === "live" &&
              failedSocket.readyState === WebSocket.OPEN &&
              workletNodeRef.current !== null &&
              !hasEndedRef.current
            ) {
              beginLiveHangupWaitRef.current(failedSocket, false)
              break
            }
            // Fail closed: a fatal service frame must not leave microphone
            // capture active behind an error-looking UI.
            if (failedSocket) {
              expectedSocketCloseRef.current = failedSocket
              failedSocket.onmessage = null
              failedSocket.close()
            }
            finishVoiceModeRef.current()
            break
          }

          case "session.created":
            logger.info("Voice mode session created")
            break

          // Backend created a new chat session for this realtime conversation
          case "session_created":
            logger.info("Chat session created for voice mode:", msg.session_id)
            if (typeof msg.session_id === "string" && msg.session_id) {
              // Remember it here too: a Live reconnect can outrun the parent's
              // next render and must not open a second chat session.
              liveBackendSessionIdRef.current = msg.session_id
            }
            callbacksRef.current?.onSessionCreated?.(msg.session_id)
            break

          // Tool execution events
          case "tool_call_started":
            logger.info("Tool call started:", msg.name)
            setCurrentTool(msg.name)
            setState("using_tools")
            if (!isVoicePlumbingTool(msg.name)) {
              pushTranscript({
                type: "work_started",
                name: msg.name,
                ...(typeof msg.call_id === "string" && msg.call_id ? { callId: msg.call_id } : {}),
                activity: createToolActivity(msg.name),
              })
            }
            callbacksRef.current?.onToolStart?.(msg.name)
            break

          case "tool_executing": {
            logger.info("Tool executing:", msg.name)
            setCurrentTool(msg.name)
            setState("using_tools")
            clearInactivityTimer()
            const budgetSeconds =
              typeof msg.timeout_seconds === "number" && Number.isFinite(msg.timeout_seconds)
                ? msg.timeout_seconds
                : null
            const executingProgress: VoiceToolProgress = {
              name: typeof msg.name === "string" ? msg.name : "",
              callId: typeof msg.call_id === "string" && msg.call_id ? msg.call_id : null,
              startedAt: Date.now(),
              stalled: false,
            }
            toolProgressRef.current = executingProgress
            setToolProgress(executingProgress)
            pushVoiceActivityRef.current(createToolActivity(msg.name))
            if (!isVoicePlumbingTool(msg.name)) {
              pushTranscript({
                type: "work_started",
                name: executingProgress.name,
                ...(executingProgress.callId ? { callId: executingProgress.callId } : {}),
                activity: createToolActivity(msg.name),
              })
            }
            armToolWatchdogRef.current(budgetSeconds)
            break
          }

          case "tool_completed":
            if (
              msg.status === "started" ||
              (typeof msg.job_id === "string" && activeBackgroundJobsRef.current.has(msg.job_id))
            ) {
              logger.info("Realtime tool detached into background work", { name: msg.name })
              break
            }
            logger.info("Tool completed", { name: msg.name, success: msg.success })
            if (!isVoicePlumbingTool(msg.name)) {
              pushTranscript({
                type: "work_finished",
                name: msg.name,
                success: msg.success ?? true,
                ...(typeof msg.call_id === "string" && msg.call_id ? { callId: msg.call_id } : {}),
              })
            }
            callbacksRef.current?.onToolComplete?.({
              name: msg.name,
              success: msg.success ?? true,
              assistantFiles: sanitizeAssistantFiles(msg.assistant_files),
              ...(typeof msg.call_id === "string" && msg.call_id ? { callId: msg.call_id } : {}),
            })
            // Short synchronous tools (check_background_work) complete without a
            // job_id while a detached read is still running. Only that job's
            // original terminal event or watchdog deadline may clear its overlay.
            if (!restoreBackgroundToolStateRef.current()) {
              releaseToolStateRef.current()
            }
            // Stay in using_tools state if there might be more tools
            // The response.done or next audio will change state
            break

          case "tool_aborted":
            // The backend dropped a superseded call; its result is deliberately
            // withheld from persistence. Unlike `tool_completed`, no successor
            // `response.done` follows an abandoned turn, so this is the only
            // chance to leave the tool state — unless a detached job is still
            // running and owns the overlay until its own terminal event.
            logger.info("Tool aborted", { name: msg.name, reason: msg.reason })
            if (!isVoicePlumbingTool(msg.name)) {
              pushTranscript({
                type: "work_aborted",
                name: msg.name,
                ...(typeof msg.call_id === "string" && msg.call_id ? { callId: msg.call_id } : {}),
              })
            }
            if (!restoreBackgroundToolStateRef.current()) {
              releaseToolStateRef.current()
              setState((current) => (current === "using_tools" ? "listening" : current))
            }
            break

          // A slow read now runs detached from the turn, so its `tool_completed`
          // arrives immediately with a "started" receipt. Without these frames
          // the surface would go blank for exactly the long lookups the feed
          // exists to narrate.
          case "voice_background_job": {
            const jobName = typeof msg.name === "string" ? msg.name : ""
            const jobId = typeof msg.job_id === "string" ? msg.job_id : ""
            if (msg.state === "running" && jobId) {
              const now = Date.now()
              const activeJob = {
                name: jobName,
                startedAt: activeBackgroundJobsRef.current.get(jobId)?.startedAt ?? now,
                watchdogDeadline:
                  activeBackgroundJobsRef.current.get(jobId)?.watchdogDeadline ??
                  now + VOICE_TOOL_WATCHDOG_FALLBACK_MS,
              }
              activeBackgroundJobsRef.current.set(jobId, activeJob)
              setCurrentTool(jobName)
              const runningProgress: VoiceToolProgress = {
                name: jobName,
                callId: jobId,
                startedAt: activeJob.startedAt,
                stalled: false,
              }
              toolProgressRef.current = runningProgress
              setToolProgress(runningProgress)
              clearInactivityTimer()
              pushVoiceActivityRef.current(createToolActivity(jobName))
              armToolWatchdogRef.current(null, jobId, activeJob.watchdogDeadline - now)
              setState((current) =>
                current === "ai_speaking" || current === "user_speaking" || current === "processing"
                  ? current
                  : "using_tools"
              )
            } else {
              if (jobId) activeBackgroundJobsRef.current.delete(jobId)
              // The detached call's `tool_completed` said "started"; this is
              // the event that actually ends it on the record.
              if (jobName && !isVoicePlumbingTool(jobName)) {
                pushTranscript({
                  type: "work_finished",
                  name: jobName,
                  success: msg.state === "completed",
                })
              }
              if (!restoreBackgroundToolStateRef.current()) {
                releaseToolStateRef.current()
                setState((current) => (current === "using_tools" ? "listening" : current))
              }
            }
            break
          }

          case "voice_tool_progress":
            setToolProgress((current) =>
              current && (current.callId === null || current.callId === msg.call_id)
                ? { ...current, stalled: true }
                : current
            )
            break

          // Narration from a direct voice tool (`tool_activity`) and from the
          // nested chat agent, whose `send_activity_update` resolves the
          // outbound writer of *this* socket and so lands here as `activity`.
          case "tool_activity":
          case "activity": {
            const entry = normalizeVoiceActivityFrame(msg)
            if (entry) {
              pushVoiceActivityRef.current(entry)
              pushTranscript({ type: "activity", activity: entry })
            }
            break
          }

          default:
            if (msg.type) {
              logger.debug("Realtime event:", msg.type)
            }
        }
      } catch {
        // A JSON parse error can retain a snippet of the provider frame. Keep
        // telemetry content-free because voice frames may contain transcripts.
        logger.error("Failed to parse realtime message")
      }
    },
    [
      clearInactivityTimer,
      discardResponseAudio,
      discardActiveUnverifiedTurn,
      queueAudioChunk,
      stopAudioPlayback,
      truncateResponse,
      isActiveResponse,
      isCompletableResponse,
      isLiveInterruptedDisplayHeld,
      playNextInQueue,
      pushTranscript,
    ]
  )
  const handleMessageRef = useRef(handleMessage)
  handleMessageRef.current = handleMessage

  // Start voice mode - connect and begin listening
  const startVoiceMode = useCallback(() => {
    const activeSocket = wsRef.current
    if (
      activeSocket?.readyState === WebSocket.CONNECTING ||
      activeSocket?.readyState === WebSocket.OPEN
    ) {
      return
    }
    activeSocket?.close()

    // A reconnect keeps the session's Live bookkeeping (one retry per voice
    // session, the chat session already minted); a fresh start clears it.
    const isLiveReconnect = liveReconnectPendingRef.current
    liveReconnectPendingRef.current = false
    if (!isLiveReconnect) {
      liveReconnectUsedRef.current = false
      liveUsageWarnedRef.current = false
      liveBackendSessionIdRef.current = null
      // A fresh call starts with an empty record; a reconnect keeps what was said.
      voiceTranscriptRef.current = []
      setVoiceTranscript([])
      setSessionStartedAt(null)
    }
    liveActiveDelegationsRef.current.clear()
    liveUserSpeakingRef.current = false
    liveUserTranscriptRef.current = ""

    clearSafetyTimers()
    hasEndedRef.current = false
    setState("connecting")
    setUserTranscript("")
    setAssistantTranscript("")
    toolProgressRef.current = null
    setToolProgress(null)
    setVoiceActivities([])
    currentAssistantTranscriptRef.current = ""

    // Connect to WebSocket with authentication
    const protocol = window.location.protocol === "https:" ? "wss:" : "ws:"
    const host = window.location.host
    const routePath = engineRef.current === "live" ? "/api/ws/live" : "/api/ws/realtime"
    let url = `${protocol}//${host}${routePath}`

    // Add authentication token to URL
    const settingsService = SettingsService.getInstance()
    const token = settingsService.token
    if (token) {
      url = `${url}?token=${encodeURIComponent(token)}`
      logger.debug("Voice mode: Added authentication token to WebSocket URL")
    } else {
      logger.warn("Voice mode: No authentication token available for WebSocket connection")
    }

    const ws = new WebSocket(url)
    wsRef.current = ws
    ws.binaryType = "arraybuffer"

    ws.onopen = () => {
      if (wsRef.current !== ws) return
      logger.info("Voice mode: Connected to Realtime API")

      // Live fixes model, instructions and voice at `session.start`, so the
      // client opens with `live.start` and never sends `session.update`.
      // Realtime configures server VAD for automatic turn detection.
      // Note: Backend injects transcription model, system instructions, and tools automatically
      const backendSessionId = engineRef.current === "live" ? liveBackendSessionIdRef.current : null
      const liveSessionId = sessionIdRef.current ?? backendSessionId ?? undefined
      const initMsg =
        engineRef.current === "live"
          ? buildLiveStartFrame(selectedVoiceRef.current, hasVoiceInternalDataConsent(), {
              routeId: sessionTypeRef.current,
              sessionType: sessionTypeRef.current,
              sessionId: liveSessionId,
            })
          : buildRealtimeSessionUpdate(selectedVoiceRef.current, hasVoiceInternalDataConsent(), {
              routeId: sessionTypeRef.current,
              sessionType: sessionTypeRef.current,
            })
      ws.send(JSON.stringify(initMsg))

      // Send session context if available
      const activeSessionId = sessionIdRef.current ?? backendSessionId
      if (activeSessionId) {
        ws.send(
          JSON.stringify({
            type: "session_context",
            session_id: activeSessionId,
            ...(sessionTypeRef.current ? { session_type: sessionTypeRef.current } : {}),
          })
        )
      }

      // Start microphone capture
      void (async () => {
        let stream: MediaStream | null = null
        try {
          initAudioContext()
          // Ask for the processed capture path explicitly. With a bare
          // `audio: true` the assistant's own speaker output can re-enter the
          // microphone, and semantic VAD (eagerness "high", interrupt_response
          // true) then reads that echo as the user barging in — which cancels
          // the turn that is currently speaking and discards its in-flight work.
          stream = await navigator.mediaDevices.getUserMedia({
            audio: {
              echoCancellation: true,
              noiseSuppression: true,
              autoGainControl: true,
            },
          })
          // The permission prompt is async: voice mode may have been stopped (or the
          // component unmounted) while it was open. If this socket is no longer the
          // active one, abandon the late-resolving stream so the mic doesn't stay hot
          // and we don't resurrect the overlay onto a closed socket.
          if (wsRef.current !== ws || ws.readyState !== WebSocket.OPEN) {
            stream.getTracks().forEach((track) => track.stop())
            return
          }
          mediaStreamRef.current = stream
          const ctx = audioContextRef.current!

          // Initialize Audio Worklet
          const blob = new Blob([WORKLET_CODE], { type: "application/javascript" })
          const blobUrl = URL.createObjectURL(blob)
          try {
            await ctx.audioWorklet.addModule(blobUrl)
          } finally {
            URL.revokeObjectURL(blobUrl)
          }

          // Re-check after the worklet module load await for the same reason.
          // Read readyState through a widened `number` so TS does not carry the
          // literal-`OPEN` narrowing from the guard above across the await (the
          // socket really can close while addModule is pending).
          const readyStateAfterModule: number = ws.readyState
          if (wsRef.current !== ws || readyStateAfterModule !== WebSocket.OPEN) {
            stream.getTracks().forEach((track) => track.stop())
            if (mediaStreamRef.current === stream) mediaStreamRef.current = null
            return
          }

          const source = ctx.createMediaStreamSource(stream)
          const workletNode = new AudioWorkletNode(ctx, "pcm-processor")

          workletNode.port.onmessage = (event) => {
            if (wsRef.current !== ws || ws.readyState !== WebSocket.OPEN) return

            const buffer = event.data
            const bytes = new Uint8Array(buffer)

            // Convert to base64
            let binary = ""
            const len = bytes.byteLength
            for (let i = 0; i < len; i++) {
              binary += String.fromCharCode(bytes[i])
            }
            const base64Audio = btoa(binary)

            // Send audio to server - VAD will handle when to commit
            const msg = {
              type: "input_audio_buffer.append",
              audio: base64Audio,
            }
            ws.send(JSON.stringify(msg))
          }

          source.connect(workletNode)
          workletNode.connect(ctx.destination)

          // Connect microphone to analyser for visualization
          if (analyserNodeRef.current) {
            source.connect(analyserNodeRef.current)
          }

          sourceNodeRef.current = source
          workletNodeRef.current = workletNode

          setState("listening")
          startSafetyTimersRef.current()
          callbacksRef.current?.onVoiceModeStart?.()
          logger.info("Voice mode: Microphone active, listening...")
        } catch (error) {
          stream?.getTracks().forEach((track) => track.stop())
          if (wsRef.current !== ws) return
          if (mediaStreamRef.current === stream) mediaStreamRef.current = null
          logger.error("Voice mode: Failed to start microphone", {
            errorType: error instanceof Error ? error.name : "unknown",
          })
          callbacksRef.current?.onError?.("microphone_access_failed")
          ws.onmessage = null
          expectedSocketCloseRef.current = ws
          ws.close()
          finishVoiceModeRef.current()
        }
      })()
    }

    ws.onmessage = (event) => {
      if (wsRef.current !== ws) return
      handleMessageRef.current(event)
    }

    ws.onclose = () => {
      if (wsRef.current !== ws) return
      const expectedClose = expectedSocketCloseRef.current === ws
      if (!expectedClose) {
        if (engineRef.current === "live" && !liveReconnectUsedRef.current) {
          logger.warn("Live voice transport closed unexpectedly; retrying once")
          scheduleLiveReconnectRef.current("unexpected_close")
          return
        }
        discardActiveUnverifiedTurn("unexpected_close")
        callbacksRef.current?.onError?.("connection_failed")
      }
      logger.info("Voice mode: Disconnected")
      finishVoiceModeRef.current()
    }

    ws.onerror = () => {
      if (wsRef.current !== ws) return
      // Do not log the event target: it contains the authenticated WebSocket URL.
      logger.error("Voice mode: WebSocket error")
      if (hangingUpRef.current) {
        // The caller already hung up; a socket failing while the route closes
        // is the end of the call, never a reason to start another one.
        ws.onmessage = null
        ws.close()
        finishVoiceModeRef.current()
        return
      }
      if (engineRef.current === "live" && !liveReconnectUsedRef.current) {
        // A drop usually surfaces here before `onclose`, so the retry has to be
        // decided here too. Quarantine the socket first, including its close
        // handler, so the retry is not cancelled by its own teardown.
        logger.warn("Live voice transport failed; retrying once")
        ws.onmessage = null
        ws.onclose = null
        expectedSocketCloseRef.current = ws
        ws.close()
        scheduleLiveReconnectRef.current("socket_error")
        return
      }
      discardActiveUnverifiedTurn("socket_error")
      callbacksRef.current?.onError?.("connection_failed")
      // Error does not guarantee a close event. Quarantine the failed socket
      // immediately so retained handlers cannot mutate a replacement session.
      ws.onmessage = null
      expectedSocketCloseRef.current = ws
      ws.close()
      finishVoiceModeRef.current()
    }
  }, [clearSafetyTimers, discardActiveUnverifiedTurn, initAudioContext])
  startVoiceModeRef.current = startVoiceMode

  /**
   * Tell the Live route the user hung up so it can close the upstream session
   * and persist the conversation. Realtime has no such frame and sends none.
   */
  const sendLiveHangup = useCallback((socket: WebSocket) => {
    if (engineRef.current !== "live") return
    try {
      sendVoiceFrame(socket, "live", { type: "voice_hangup" })
    } catch (error) {
      logger.warn("Voice mode: Could not announce the Live hang-up", {
        errorType: error instanceof Error ? error.name : "unknown",
      })
    }
  }, [])
  sendLiveHangupRef.current = sendLiveHangup

  /**
   * After a Live call has started, the route persists before it sends
   * `live_session_closed`. Keep the socket and `onmessage` until that frame
   * (or the grace timer) so `onVoiceModeEnd` does not refetch history early.
   * The call goes quiet at once: no microphone, no playback.
   */
  const beginLiveHangupWait = useCallback(
    (socket: WebSocket, sendHangup: boolean) => {
      if (hangingUpRef.current || hasEndedRef.current) return
      hangingUpRef.current = true
      setIsHangingUp(true)
      if (sendHangup) {
        sendLiveHangupRef.current(socket)
      }
      expectedSocketCloseRef.current = socket
      stopAudioPlayback()
      mediaStreamRef.current?.getTracks().forEach((track) => track.stop())
      workletNodeRef.current?.disconnect()
      sourceNodeRef.current?.disconnect()
      liveHangupTimerRef.current = setTimeout(() => {
        liveHangupTimerRef.current = null
        logger.warn("Voice mode: the Live route did not confirm the close in time")
        if (wsRef.current === socket) {
          socket.onmessage = null
          socket.close()
        }
        finishVoiceModeRef.current()
      }, LIVE_HANGUP_GRACE_MS)
    },
    [stopAudioPlayback]
  )
  beginLiveHangupWaitRef.current = beginLiveHangupWait

  /**
   * Release the transport and every resource behind it — socket, microphone,
   * worklet, AudioContext, timers — without telling the caller the voice
   * session is over. A Live reconnect uses this alone; every real ending goes
   * through `finishVoiceMode`, which adds the `onVoiceModeEnd` callback.
   */
  const teardownTransport = useCallback(() => {
    clearSafetyTimers()
    clearLiveTimers()

    // Stop audio playback
    stopAudioPlayback()

    // Stop microphone
    if (mediaStreamRef.current) {
      mediaStreamRef.current.getTracks().forEach((track) => track.stop())
      mediaStreamRef.current = null
    }

    if (workletNodeRef.current) {
      workletNodeRef.current.disconnect()
      workletNodeRef.current = null
    }

    if (sourceNodeRef.current) {
      sourceNodeRef.current.disconnect()
      sourceNodeRef.current = null
    }

    // Cleanup analyser node
    if (analyserNodeRef.current) {
      analyserNodeRef.current.disconnect()
      analyserNodeRef.current = null
    }

    // Close and drop the AudioContext so each voice session starts fresh.
    // The context is otherwise reused across sessions, and the second session's
    // addModule() re-registers the 'pcm-processor' worklet on the same context,
    // which throws (NotSupportedError) and breaks voice mode until a remount.
    if (audioContextRef.current) {
      void audioContextRef.current.close().catch(() => {
        // Context may already be closed; nothing to recover.
      })
      audioContextRef.current = null
    }
    nextPlayTimeRef.current = 0
    finalizedResponseIdRef.current = null
    currentResponseInterruptedRef.current = false
    currentResponseIdRef.current = null
    currentResponseInputItemIdRef.current = null
    liveAnswerInputItemIdRef.current = null
    currentItemIdRef.current = null
    expectedSocketCloseRef.current = null
    voiceToolApprovalsRef.current = []
    voiceToolApprovalSubmittingRef.current = null
    activeBackgroundJobsRef.current.clear()

    wsRef.current = null
    releaseToolState()
    setVoiceActivities([])
    setVoiceToolApprovals([])
    setIsVoiceToolApprovalSubmitting(false)
    hangingUpRef.current = false
    setIsHangingUp(false)
    setState("idle")
  }, [clearLiveTimers, clearSafetyTimers, releaseToolState, stopAudioPlayback])

  const finishVoiceMode = useCallback(() => {
    if (hasEndedRef.current) return
    hasEndedRef.current = true
    teardownTransport()
    callbacksRef.current?.onVoiceModeEnd?.()
  }, [teardownTransport])
  finishVoiceModeRef.current = finishVoiceMode

  /**
   * One automatic retry after a Live transport drop. The voice session stays
   * open from the caller's point of view — `onVoiceModeEnd` must not fire —
   * but the in-flight turn was never confirmed by the backend, so it is
   * discarded before the transport goes away.
   */
  const scheduleLiveReconnect = useCallback(
    (source: RealtimeSessionFailureSource) => {
      liveReconnectUsedRef.current = true
      // Discard first: teardown clears the refs the discard reads.
      discardActiveUnverifiedTurn(source)
      teardownTransport()
      pushTranscript({ type: "work_settle" })
      pushTranscript({ type: "system", code: "connection_lost" })
      setState("reconnecting")
      liveReconnectTimerRef.current = setTimeout(() => {
        liveReconnectTimerRef.current = null
        liveReconnectPendingRef.current = true
        startVoiceModeRef.current()
      }, LIVE_RECONNECT_DELAY_MS)
    },
    [discardActiveUnverifiedTurn, teardownTransport]
  )
  scheduleLiveReconnectRef.current = scheduleLiveReconnect

  const endVoiceModeForSafety = useCallback(
    (reason: RealtimeAutoHangupReason) => {
      if (hasEndedRef.current || hangingUpRef.current) return
      logger.info("Voice mode: Safety hang-up", { reason })
      callbacksRef.current?.onAutoHangup?.(reason)
      const activeSocket = wsRef.current
      if (
        activeSocket &&
        engineRef.current === "live" &&
        activeSocket.readyState === WebSocket.OPEN &&
        workletNodeRef.current !== null
      ) {
        beginLiveHangupWait(activeSocket, true)
        return
      }
      if (activeSocket) {
        sendLiveHangupRef.current(activeSocket)
        expectedSocketCloseRef.current = activeSocket
        activeSocket.onmessage = null
        activeSocket.close()
      }
      finishVoiceMode()
    },
    [beginLiveHangupWait, finishVoiceMode]
  )
  endVoiceModeForSafetyRef.current = endVoiceModeForSafety

  // Stop voice mode
  const stopVoiceMode = useCallback(() => {
    // The route is already closing this call: a second tap, Escape or the
    // button must not cut the wait short, or history is refetched before the
    // save. Unmount and the grace timer are the only ways past it.
    if (hangingUpRef.current) return
    const ws = wsRef.current
    const callIsLive =
      engineRef.current === "live" &&
      ws?.readyState === WebSocket.OPEN &&
      workletNodeRef.current !== null &&
      !hasEndedRef.current
    if (ws && callIsLive) {
      // Announce the hang-up and let the route close and persist first. The
      // call goes quiet at once: no microphone, no playback, no more audio
      // accepted. The session ends on `live_session_closed`, or on the timer.
      // (A call still acquiring its microphone has nothing to save and takes
      // the immediate path below, which is what its pending guards rely on.)
      beginLiveHangupWait(ws, true)
      return
    }
    if (ws) {
      sendLiveHangupRef.current(ws)
      expectedSocketCloseRef.current = ws
      ws.close()
    }
    finishVoiceMode()
  }, [beginLiveHangupWait, finishVoiceMode])

  // Unmounting during the hang-up wait must not leave the socket and its
  // timer behind: `stopVoiceMode` is a no-op by then, so end it here.
  useEffect(() => {
    return () => {
      if (!hangingUpRef.current) return
      const ws = wsRef.current
      if (ws) {
        ws.onmessage = null
        ws.close()
      }
      finishVoiceModeRef.current()
    }
  }, [])

  // Refresh backend-owned routing context when the page adopts a session.
  useEffect(() => {
    if (wsRef.current?.readyState !== WebSocket.OPEN || (!sessionId && !sessionType)) return
    wsRef.current.send(
      JSON.stringify({
        type: "session_context",
        ...(sessionId ? { session_id: sessionId } : {}),
        ...(sessionType ? { session_type: sessionType } : {}),
      })
    )
    logger.info("Voice mode: Refreshed backend session context", {
      hasSessionId: Boolean(sessionId),
      hasSessionType: Boolean(sessionType),
    })
  }, [sessionId, sessionType])

  // Toggle voice mode
  const toggleVoiceMode = useCallback(() => {
    if (state === "idle") {
      void startVoiceMode()
    } else {
      stopVoiceMode()
    }
  }, [state, startVoiceMode, stopVoiceMode])

  // Computed states
  const isInVoiceMode = state !== "idle" && state !== "connecting"
  const isConnecting = state === "connecting"
  const isListening = state === "listening"
  const isUserSpeaking = state === "user_speaking"
  const isAISpeaking = state === "ai_speaking"
  const isProcessing = state === "processing"
  const isUsingTools = state === "using_tools"

  // Send session_context to backend immediately (for when frontend creates a session during voice mode)
  const sendSessionContext = useCallback((newSessionId: string) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(
        JSON.stringify({
          type: "session_context",
          session_id: newSessionId,
          ...(sessionTypeRef.current ? { session_type: sessionTypeRef.current } : {}),
        })
      )
      logger.info("Voice mode: Sent session_context immediately", { sessionId: newSessionId })
    } else {
      logger.warn("Voice mode: Cannot send session_context, WebSocket not open", {
        sessionId: newSessionId,
        wsState: wsRef.current?.readyState,
      })
    }
  }, [])

  const sendVoiceToolDecision = useCallback((approved: boolean) => {
    const approval = voiceToolApprovalsRef.current.at(0)
    const socket = wsRef.current
    if (
      !approval ||
      approval.status !== "pending" ||
      voiceToolApprovalSubmittingRef.current !== null ||
      !socket ||
      socket.readyState !== WebSocket.OPEN
    ) {
      return false
    }

    voiceToolApprovalSubmittingRef.current = `${approval.sessionId}:${approval.request_id}:${approval.call_id}`
    setIsVoiceToolApprovalSubmitting(true)
    try {
      socket.send(
        JSON.stringify({
          type: "voice_tool_decision",
          session_id: approval.sessionId,
          request_id: approval.request_id,
          call_id: approval.call_id,
          approved,
          source: "screen",
        })
      )
      return true
    } catch (error) {
      voiceToolApprovalSubmittingRef.current = null
      setIsVoiceToolApprovalSubmitting(false)
      logger.warn("Voice mode: Could not send tool approval decision", {
        errorType: error instanceof Error ? error.name : "unknown",
      })
      return false
    }
  }, [])

  return {
    // State
    state,
    isInVoiceMode,
    isConnecting,
    isListening,
    isUserSpeaking,
    isAISpeaking,
    isProcessing,
    isUsingTools,
    isPlayingAudio,
    currentTool,
    toolProgress,
    voiceActivities,
    voiceToolApprovals,
    voiceToolApproval: voiceToolApprovals.at(0) ?? null,
    isVoiceToolApprovalSubmitting,

    // Transcripts
    userTranscript,
    assistantTranscript,
    voiceTranscript,
    sessionStartedAt,
    isHangingUp,

    // Audio visualization
    analyserNode: analyserNodeRef.current,

    // Actions
    startVoiceMode,
    stopVoiceMode,
    toggleVoiceMode,
    sendSessionContext,
    sendVoiceToolDecision,
  }
}
